import {useCallback, useMemo, useState} from 'react';
import {useApiCalls} from '../../../../../../hooks/init-calls';
import {useStoreContext} from '../../../../../../store/store-context';
import {activateNotification} from '../../../../../../ui/molecules/notification/activate-notification';
import {generateRandomDigits} from '../../../../../../utils/generate-random-numbers';
import {setFirebaseData} from '../../../../../../utils/firebase-handler';
import {groupTypeController} from '../admin-group-type/admin-group-type-controller';
import {groupTypeRequestImpl} from '../admin-group-type/admin-group-type-request';
import {groupController} from './admin-group-controller';
import {groupRequestImpl} from './admin-group-request';

export type CreateGroupTypeOption = {
  id: string;
  name: string;
};

type CreateGroupDialogOptions = {
  groupTypes: CreateGroupTypeOption[];
  onClose: () => void;
  onCreated: () => Promise<void> | void;
};

type CreateGroupErrors = {
  name?: string;
  groupAdmin?: string;
  groupType?: string;
  customTypeName?: string;
};

export const normalizeGroupTypeName = (value: string) =>
  value.trim().toLocaleLowerCase();

export const findGroupTypeByName = (
  groupTypes: CreateGroupTypeOption[],
  name: string,
) =>
  groupTypes.find(
    (type) => normalizeGroupTypeName(type.name) === normalizeGroupTypeName(name),
  );

export const buildGroupMemberIds = (groupAdmin: string, users: string[]) =>
  Array.from(new Set([groupAdmin, ...users].filter(Boolean)));

const normalizeGroupTypes = (response: any): CreateGroupTypeOption[] => {
  const values = Array.isArray(response)
    ? response
    : Array.isArray(response?.results)
    ? response.results
    : Array.isArray(response?.data)
    ? response.data
    : [];

  return values.filter((type: any) => type?.id && type?.name);
};

export const useCreateGroupDialog = ({
  groupTypes,
  onClose,
  onCreated,
}: CreateGroupDialogOptions) => {
  const [name, setName] = useState('');
  const [groupAdmin, setGroupAdmin] = useState('');
  const [users, setUsers] = useState<string[]>([]);
  const [groupTypeId, setGroupTypeId] = useState('');
  const [customTypeName, setCustomTypeName] = useState('');
  const [isCustomType, setIsCustomType] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<CreateGroupErrors>({});
  const {initGroups} = useApiCalls();
  const {setInitResponseAsAuth} = useStoreContext().authStore;

  const reset = useCallback(() => {
    setName('');
    setGroupAdmin('');
    setUsers([]);
    setGroupTypeId('');
    setCustomTypeName('');
    setIsCustomType(false);
    setErrors({});
  }, []);

  const close = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, onClose, reset]);

  const selectExistingType = useCallback((id: string) => {
    setGroupTypeId(id);
    setIsCustomType(false);
    setErrors((current) => ({
      ...current,
      groupType: undefined,
      customTypeName: undefined,
    }));
  }, []);

  const selectCustomType = useCallback(() => {
    setGroupTypeId('');
    setIsCustomType(true);
    setErrors((current) => ({...current, groupType: undefined}));
  }, []);

  const canSubmit = useMemo(
    () =>
      Boolean(
        name.trim() &&
          groupAdmin &&
          (groupTypeId || (isCustomType && customTypeName.trim())),
      ),
    [customTypeName, groupAdmin, groupTypeId, isCustomType, name],
  );

  const validate = useCallback(() => {
    const nextErrors: CreateGroupErrors = {};

    if (!name.trim()) nextErrors.name = 'Enter a group name';
    if (!groupAdmin) nextErrors.groupAdmin = 'Select a group lead';
    if (!groupTypeId && !isCustomType) {
      nextErrors.groupType = 'Select a group type';
    }

    if (isCustomType) {
      if (!customTypeName.trim()) {
        nextErrors.customTypeName = 'Enter a custom group type';
      } else if (findGroupTypeByName(groupTypes, customTypeName)) {
        nextErrors.customTypeName = 'This group type already exists';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [customTypeName, groupAdmin, groupTypeId, groupTypes, isCustomType, name]);

  const submit = useCallback(async () => {
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      let selectedTypeId = groupTypeId;

      if (isCustomType) {
        const typeController = new groupTypeController(
          new groupTypeRequestImpl(),
        );
        const trimmedTypeName = customTypeName.trim();

        await typeController.newCreateGroupType(
          {name: trimmedTypeName},
          true,
        );
        const latestTypes = normalizeGroupTypes(
          await typeController.newfetchGroupType(),
        );
        const createdType = findGroupTypeByName(latestTypes, trimmedTypeName);

        if (!createdType) {
          throw new Error('The new group type could not be loaded.');
        }

        selectedTypeId = createdType.id;
        setGroupTypeId(createdType.id);
        setIsCustomType(false);
        setFirebaseData(`updates/groupType`, generateRandomDigits(16));
      }

      const controller = new groupController(new groupRequestImpl());
      await controller.newCreateGroup(
        {
          name: name.trim(),
          groupAdmin,
          groupType: selectedTypeId,
          users: buildGroupMemberIds(groupAdmin, users),
        },
        true,
      );

      setFirebaseData(`updates/group`, generateRandomDigits(16));
      setInitResponseAsAuth();
      await initGroups();
      await onCreated();
      activateNotification({
        title: 'Success',
        content: 'Group has been created',
        kind: 'success',
      });
      reset();
      onClose();
    } catch (error: any) {
      if (error?.message === 'The new group type could not be loaded.') {
        activateNotification({
          title: 'Error',
          content: error.message,
          kind: 'error',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    customTypeName,
    groupAdmin,
    groupTypeId,
    initGroups,
    isCustomType,
    isSubmitting,
    name,
    onClose,
    onCreated,
    reset,
    setInitResponseAsAuth,
    users,
    validate,
  ]);

  return {
    canSubmit,
    close,
    customTypeName,
    errors,
    groupAdmin,
    groupTypeId,
    isCustomType,
    isSubmitting,
    name,
    selectCustomType,
    selectExistingType,
    setCustomTypeName,
    setGroupAdmin,
    setName,
    submit,
    users,
    setUsers,
  };
};
